import { useEffect, useRef, useState, createContext, useContext } from 'react';
import { gsap } from 'gsap';

// Context to share accordion state
const AccordionContext = createContext();

// Reusable Accordion Components
const Accordion = ({ children, className = '', defaultOpen = false }) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	const toggleAccordion = () => {
		setIsOpen(!isOpen);
	};

	return (
		<AccordionContext.Provider value={{ isOpen, toggleAccordion }}>
			<div className={className} is-open={isOpen.toString()}>
				{children}
			</div>
		</AccordionContext.Provider>
	);
};

const AccordionTrigger = ({ children, className = '' }) => {
	const { isOpen, toggleAccordion } = useContext(AccordionContext);
	const iconRef = useRef(null);

	useEffect(() => {
		if (iconRef.current) {
			gsap.to(iconRef.current, {
				rotation: isOpen ? -180 : 0,
				duration: 0.2,
				ease: 'power2.inOut'
			});
		}
	}, [isOpen]);

	return (
		<div 
			className={className}
			onClick={toggleAccordion}
		>
			{children}
			<span 
				ref={iconRef}
				className="font-MaterialIcons text-grey text-3xl font-thin leading-none ml-auto"
				style={{ 
					display: 'inline-block'
				}}
			>
				keyboard_arrow_down
			</span>
		</div>
	);
};

const AccordionBody = ({ children, className = '' }) => {
	const { isOpen } = useContext(AccordionContext);
	const contentRef = useRef(null);
	const contentInnerRef = useRef(null);

	useEffect(() => {
		if (contentRef.current && contentInnerRef.current) {
			const height = contentInnerRef.current.offsetHeight;
			
			if (isOpen) {
				gsap.to(contentRef.current, {
					height: height,
					opacity: 1,
					duration: 0.3,
					ease: 'power2.easeInOut',
					onComplete: () => {
						// Set to auto after animation for responsive behavior
						contentRef.current.style.height = 'auto';
					}
				});
			} else {
				gsap.to(contentRef.current, {
					height: 0,
					opacity: 0,
					duration: 0.2,
					ease: 'power2.easeInOut'
				});
			}
		}
	}, [isOpen]);

	return (
		<div 
			ref={contentRef}
			className={`overflow-hidden ${className}`}
			style={{ height: 0, opacity: 0 }}
		>
			<div ref={contentInnerRef}>
				{children}
			</div>
		</div>
	);
};

export { Accordion, AccordionTrigger, AccordionBody };